/* eslint-disable no-mixed-spaces-and-tabs */
import React, { FC, useMemo, useCallback } from "react";
import styled from "styled-components";

import Button from "@ingka/button";
import Text from "@ingka/text";
import ListView, { ListViewItem } from "@ingka/list-view";
import laptop from "@ingka/ssr-icon/paths/laptop";
import arrowLeftFromBase from "@ingka/ssr-icon/paths/arrow-left-from-base";
import Modal, { Sheets, ModalBody, ModalFooter, Prompt, ModalHeader } from "@ingka/modal";
import documentUpload from "@ingka/ssr-icon/paths/document-upload";
import Toast from "@ingka/toast";
import { colourNeutral3, colourSemanticInformative, colourStaticIkeaBrandYellow } from "@ingka/variables/colours-css";
import person from "@ingka/ssr-icon/paths/person";
import mail from "@ingka/ssr-icon/paths/mail";
import Tooltip from "@ingka/tooltip";
import trashCan from "@ingka/ssr-icon/paths/trash-can";
import Loading, { LoadingBall } from "@ingka/loading";
import { CommercialMessageVariant } from "@ingka/commercial-message";
import { space100, thicknessThin } from "@ingka/variables/design-tokens";
import SSRIcon from "@ingka/ssr-icon";

import { LoadingWrapper, SrOnly, StyledCommercialMessage, StyledModalHeader } from "../styles";
import { ROUTE_LOGOUT, UserPermissions } from "../../utils/constants";
import { User, UserApplication } from "../../store/types";
import { isUserHasMarkets } from "../../utils/common";
import FlagsRow from "../FLagsRow";
import UserApplicationOverview from "./UserApplicationOverview";
import useSelector, { userSelector } from "../../store";
import { ApplicationList, ApplicationListItemModel } from "../NewUser/Requests/ApplicationList";
import { useGetCountryName } from "../../hooks/useGetCountryName";

const StyledListView = styled(ListView)`
	padding-left: 0rem;
`;

const CustomModalHeader = styled(ModalHeader)`
	margin: 0;
	background-color: ${colourSemanticInformative};
	button .btn__inner,
	button:hover .btn__inner {
		background: transparent;
		color: white;
	}
`;

const StyledHeader = styled.h2`
	color: ${colourStaticIkeaBrandYellow};
	margin: 0;
`;

const StyledWhiteHeader = styled.span`
	color: white;
`;

const RequestedApplicationsWrapper = styled.div`
	border-bottom: 1px ${colourNeutral3} solid;
	margin-top: 1rem;
`;

const InfoRow = styled.span`
	display: flex;
	align-items: center;
	gap: 0.25rem;
`;

const StyledListViewItem = styled(ListViewItem)`
	.list-view-item__label-container {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}
`;

const StyledUserAccount = styled.div`
	display: flex;
	flex-direction: column;
`;

const AccountDetail = styled.div`
	display: flex;
	flex-grow: 1;
	align-items: center;

	border-bottom: solid ${thicknessThin} ${colourNeutral3};
	svg.svg-icon {
		color: rgb(var(--colour-text-and-icon-1));
		margin-inline-end: 1rem;
		flex-shrink: 0;
	}
`;

interface ProfileLayoutProps {
	setShowModal: (value: boolean) => void;
	showModal: boolean;
	currentUser: User | null;
	handleRemoveMe: () => void;
	setShowModalPrompt: (value: boolean) => void;
	showModalPrompt: boolean;
	toast: string;
	setToast: (val: string) => void;
	onUserApplicationClick: (value?: string) => void;
	showUserAppOverviewModal: boolean;
	setShowUserAppOverviewModal: (value: boolean) => void;
	userApplicationSelected?: UserApplication & { id: string };
	onCancelApplicationRequest: (appId: string | undefined, type: string | undefined) => void;
	setCurrentUser: (data: User | null) => void;
	cancellingApplicationRequest: boolean;
	userLoading: boolean;
	cancellingApplication: { appId: string; type: "new" | "existing" } | undefined;
	showCancelAppRequestModal: boolean;
	setShowCancelAppRequestModal: (val: boolean) => void;
	setCancellingApplication: (val: { appId: string; type: "new" | "existing" } | undefined) => void;
}

const ProfileLayout: FC<ProfileLayoutProps> = ({
	showModal,
	setShowModal,
	currentUser,
	handleRemoveMe,
	setShowModalPrompt,
	showModalPrompt,
	toast,
	setToast,
	onUserApplicationClick,
	showUserAppOverviewModal,
	setShowUserAppOverviewModal,
	userApplicationSelected,
	onCancelApplicationRequest,
	cancellingApplicationRequest,
	userLoading,
	cancellingApplication,
	showCancelAppRequestModal,
	setShowCancelAppRequestModal,
	setCancellingApplication,
}) => {
	const user = useSelector(userSelector);
	const { getCountryName } = useGetCountryName();

	const getRoleName = useCallback((userApp: UserApplication): string => {
		if (userApp && userApp?.permissions === UserPermissions.Admin) {
			return "Admin";
		} else if (userApp && userApp?.permissions === UserPermissions.Maintainer && isUserHasMarkets(userApp?.markets)) {
			return "Maintainer";
		} else if (userApp && userApp?.permissions === UserPermissions.Maintainer) {
			return "Read Only";
		}
		return "";
	}, []);

	const userRequestedApplications = useMemo<ApplicationListItemModel[]>(() => {
		return (currentUser?.user.requestedApplications ?? []).map((app) => ({
			id: app.application,
			title: app["friendly-name"] || app.application,
			onDelete: () => {
				setCancellingApplication({ appId: app.application, type: "existing" });
				setShowCancelAppRequestModal(true);
			},
			isLoading: cancellingApplicationRequest && cancellingApplication?.appId === app.application,
		}));
	}, [
		currentUser?.user.requestedApplications,
		cancellingApplicationRequest,
		cancellingApplication?.appId,
		setCancellingApplication,
		setShowCancelAppRequestModal,
	]);

	const userRequestedNewApplications = useMemo<ApplicationListItemModel[]>(() => {
		return (currentUser?.user.newApplicationRequests ?? []).map((app) => ({
			id: app.appId,
			title: app.appName || app.appId,
			onDelete: () => {
				setCancellingApplication({ appId: app.appId, type: "new" });
				setShowCancelAppRequestModal(true);
			},
			isLoading: cancellingApplicationRequest && cancellingApplication?.appId === app.appId,
		}));
	}, [
		currentUser?.user.newApplicationRequests,
		cancellingApplicationRequest,
		cancellingApplication?.appId,
		setCancellingApplication,
		setShowCancelAppRequestModal,
	]);

	const getAriaLabelForApp = (appName: string, roleName: string, marketsData: string[]) => {
		if (marketsData.length) {
			return `In application ${appName} you have role ${roleName} with access to markets: ${marketsData
				.map((market) => `${getCountryName(market)}`)
				.toString()
				.replaceAll(",", ", ")}. Click to change role`;
		}

		return `In application ${appName} you have role ${roleName}. Click to change role`;
	};

	return (
		<div>
			<Modal
				visible={showModal}
				handleCloseBtn={() => {
					setShowModal(false);
				}}
			>
				<Sheets
					id="profile-modal"
					aria-label="Profile"
					header={
						<CustomModalHeader ariaCloseTxt="Close" closeBtnClick={() => setShowModal(false)}>
							<StyledHeader>
								<StyledWhiteHeader>Hej! </StyledWhiteHeader>
								{user?.user.name.split(" ")[0]}
							</StyledHeader>
						</CustomModalHeader>
					}
					footer={
						<ModalFooter>
							<Button
								type="secondary"
								data-testid="Logout"
								text="Logout"
								ssrIcon={arrowLeftFromBase}
								href={ROUTE_LOGOUT}
							/>
							<Button
								text="Remove me from Flagga"
								data-testid="RemoveMeFromFlagga"
								type="danger"
								onClick={() => setShowModalPrompt(true)}
								aria-haspopup="dialog"
							/>
						</ModalFooter>
					}
				>
					<ModalBody>
						{userLoading ? (
							<LoadingWrapper aria-live="polite">
								<Loading labelPosition="center" text="Loading Profile" labelTransitions>
									<LoadingBall color="emphasised" />
								</Loading>
							</LoadingWrapper>
						) : (
							<>
								<StyledUserAccount id="user-info" role="group" aria-label="User details">
									<AccountDetail key={currentUser?.user.name}>
										<SSRIcon paths={person} />
										<Text textStyle="Body.M" tagName="p">
											{currentUser?.user.name}
										</Text>
									</AccountDetail>
									<AccountDetail key={currentUser?.user.email}>
										<SSRIcon paths={mail} />
										<Text textStyle="Body.M" tagName="p">
											{currentUser?.user.email}
										</Text>
									</AccountDetail>
								</StyledUserAccount>
								{!currentUser?.user["flagga-admin"] && (
									<>
										{currentUser?.user?.applications && !!Object.keys(currentUser?.user?.applications).length && (
											<RequestedApplicationsWrapper>
												<Text textStyle="Heading.S" tagName="h3">
													User applications
												</Text>
												<StyledListView showDivider={false} id="user-applications">
													{Object.keys(currentUser?.user?.applications).map((app) => {
														const roleName = getRoleName(currentUser?.user?.applications[app]);
														const variantMap: Record<string, CommercialMessageVariant> = {
															Admin: "family",
															Maintainer: "new",
															"Read Only": "nlp",
														};
														return (
															<ListViewItem
																key={app}
																showDivider={false}
																leadingIcon={laptop}
																description={
																	<InfoRow>
																		<span>Role:</span>
																		{currentUser?.user?.applications[app] != null && (
																			<StyledCommercialMessage
																				message={roleName}
																				variant={variantMap[roleName] || "new"}
																				subtle
																			/>
																		)}
																	</InfoRow>
																}
																addOn={
																	currentUser?.user?.applications[app]?.permissions === UserPermissions.Maintainer &&
																	Object.keys(currentUser?.user?.applications[app]?.markets || {}).length ? (
																		<FlagsRow
																			markets={Object.keys(currentUser?.user?.applications[app]?.markets || {})}
																			size={100}
																			tooltipBoundaryContainerID="user-applications"
																			truncateAfter={5}
																		/>
																	) : undefined
																}
																control="navigational"
																onClick={() => onUserApplicationClick(app)}
																controlIcon="chevronRightSmall"
																title={currentUser?.user?.applications[app]?.applicationName || app}
																aria-haspopup="dialog"
																aria-label={getAriaLabelForApp(
																	currentUser?.user?.applications[app]?.applicationName || app,
																	roleName,
																	Object.keys(currentUser?.user?.applications[app]?.markets || {})
																)}
															/>
														);
													})}
												</StyledListView>
											</RequestedApplicationsWrapper>
										)}
										{userRequestedApplications.length ? (
											<RequestedApplicationsWrapper>
												<Text textStyle="Heading.S" tagName="h3">
													Requests to existing applications
												</Text>
												<ApplicationList listId="user-requested-applications" items={userRequestedApplications} />
											</RequestedApplicationsWrapper>
										) : null}

										{userRequestedNewApplications.length ? (
											<RequestedApplicationsWrapper>
												<Text textStyle="Heading.S" tagName="h3">
													Requests for new applications
												</Text>
												<ApplicationList
													listId="user-new-requested-applications"
													items={userRequestedNewApplications}
												/>
											</RequestedApplicationsWrapper>
										) : null}
									</>
								)}
							</>
						)}
					</ModalBody>
				</Sheets>
			</Modal>
			<Modal visible={showModalPrompt} handleCloseBtn={() => setShowModalPrompt(false)}>
				<Prompt
					footer={
						<ModalFooter>
							<Button text="Stay with Flagga" data-autofocus type="emphasised" />
							<Button text="Remove my access" type="danger" onClick={handleRemoveMe} />
						</ModalFooter>
					}
					title="Do you want to remove your access to Flagga?"
					header={null}
					titleId="removeAccessTitle"
				/>
			</Modal>
			<Modal visible={showUserAppOverviewModal} handleCloseBtn={() => setShowUserAppOverviewModal(false)}>
				<Sheets
					id="app-details-modal"
					aria-label={`${userApplicationSelected?.applicationName} details`}
					footer={null}
					header={
						<StyledModalHeader
							title={`${userApplicationSelected?.applicationName} details`}
							backBtnClick={() => setShowUserAppOverviewModal(false)}
						/>
					}
				>
					<ModalBody>
						<UserApplicationOverview
							appId={userApplicationSelected?.id}
							setToast={setToast}
							setShowUserAppOverviewModal={setShowUserAppOverviewModal}
						/>
					</ModalBody>
				</Sheets>
			</Modal>
			<SrOnly role="status" aria-live="polite" aria-atomic="true">
				{toast}
			</SrOnly>
			<Toast
				text={toast}
				isOpen={!!toast}
				onCloseRequest={() => setToast("")}
				ariaLabelCloseBtn="Dismiss notification"
			/>
			<Modal
				data-testid="cancelApplicationRequestModal"
				visible={showCancelAppRequestModal}
				handleCloseBtn={() => setShowCancelAppRequestModal(false)}
			>
				<Prompt
					title="Cancel Application Request"
					footer={
						<ModalFooter>
							<Button
								id="confirm-cancel-request-button"
								text="Cancel Request"
								onClick={() => {
									onCancelApplicationRequest(cancellingApplication?.appId, cancellingApplication?.type);
								}}
							/>
							<Button
								id="close-modal-button"
								text="Close"
								type="emphasised"
								onClick={() => setShowCancelAppRequestModal(false)}
								aria-haspopup="dialog"
							/>
						</ModalFooter>
					}
					header={<StyledModalHeader title="Cancel Application Request!" />}
					titleId="cancelApplicationTitle"
				>
					<p>
						Do you want to cancel request to access{" "}
						<strong>
							<>
								{cancellingApplication?.type === "existing" &&
									(currentUser?.user.requestedApplications?.find(
										(app) => app.application === cancellingApplication?.appId
									)?.["friendly-name"] ||
										cancellingApplication)}
								{cancellingApplication?.type === "new" &&
									(currentUser?.user.newApplicationRequests?.find((app) => app.appId === cancellingApplication?.appId)
										?.appName ||
										cancellingApplication)}
							</>
						</strong>
						?
					</p>
				</Prompt>
			</Modal>
		</div>
	);
};

export default ProfileLayout;
